import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AppLink } from '@/components/ui/app-link';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ProcessRunCancelAction } from '@/components/shared/ProcessRunCancelAction';
import { executeTool } from '@/api/tools-api';
import type { ProcessLeaseSummary } from '@vivport/backend/types';
import {
  listProcessAgentOptions,
  ProcessRunListItem,
  ProcessRunEntryListItem,
  listProcessRunEntries,
  listProcessRuns,
  listProcessSkillOptions,
  listProcesses,
  updateProcessEnabled,
  updateProcess,
  deleteProcess,
} from '@/api/processes-api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { listMembers } from '@/api/admin-api';
import { toContentHref } from '@/utils/content-ref';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { ProcessFormValues, Work4MeProcessForm } from './Work4MeProcessForm';
import {
  DeterministicProcessFormInitialValues,
  DeterministicProcessFormValues,
  Work4MeDeterministicProcessForm,
} from './Work4MeDeterministicProcessForm';
import { useUser } from '@/hooks';
import { toast } from 'sonner';

function formatTimestamp(value: string | null): string {
  if (!value) return 'N/A';
  return new Date(value).toLocaleString();
}

function toMissionControlRunHref(agentRunId: string): string {
  return `/admin/mission-control?mcTab=runs&runId=${encodeURIComponent(agentRunId)}`;
}

const MISSION_CONTROL_APPROVALS_HREF = '/admin/mission-control?mcTab=approvals';
const LEASE_GRADUATION_MIN_CLEAN_RUNS = 5;

function leaseStateLabel(state: ProcessLeaseSummary['state']): string {
  if (state === 'expired') return 'Expired';
  if (state === 'spend_cap_reached') return 'Spend cap reached';
  if (state === 'none') return 'No lease';
  return 'Active';
}

function leaseStateVariant(state: ProcessLeaseSummary['state']): StatusBadgeVariant {
  if (state === 'expired' || state === 'spend_cap_reached') return 'destructive';
  if (state === 'none') return 'outline';
  return 'default';
}

function formatAud(value: string | null, fallback: string): string {
  return value === null ? fallback : `A$${Number(value).toFixed(2)}`;
}

interface LeaseActionInput {
  toolKey: 'processes.renewLease' | 'processes.graduateLease' | 'processes.revokeLeaseAutonomy';
  input: Record<string, unknown>;
  successText: string;
}

type StatusBadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

function runStatusVariant(status: string): StatusBadgeVariant {
  if (status === 'SUCCEEDED') return 'default';
  if (status === 'CANCELLED') return 'outline';
  if (status === 'FAILED') return 'destructive';
  if (status === 'SKIPPED') return 'secondary';
  if (status === 'RUNNING') return 'secondary';
  if (status === 'WAITING_APPROVAL') return 'secondary';
  return 'outline';
}

function stepStatusVariant(status: string): StatusBadgeVariant {
  if (status === 'SUCCEEDED') return 'default';
  if (status === 'CANCELLED') return 'outline';
  if (status === 'FAILED') return 'destructive';
  if (status === 'SKIPPED') return 'secondary';
  if (status === 'RUNNING') return 'secondary';
  return 'outline';
}

function trimForUi(value: string, max = 140): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}...`;
}

function readRuntimeStateString(run: ProcessRunListItem, key: string): string | null {
  const state = run.latestRuntimeState;
  if (!state || typeof state !== 'object' || Array.isArray(state)) return null;
  const value = (state as Record<string, unknown>)[key];
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function getRunStatusHint(run: ProcessRunListItem): string {
  if (run.status === 'FAILED') {
    return run.error ? `Failed: ${trimForUi(run.error)}` : 'Failed. Open run details for the failure reason.';
  }
  if (run.status === 'SKIPPED') {
    return (
      readRuntimeStateString(run, 'skipReason') ??
      readRuntimeStateString(run, 'summaryLine') ??
      'Skipped: the process looked at this trigger and deliberately did nothing.'
    );
  }
  if (run.status === 'CANCELLED') {
    const reason = readRuntimeStateString(run, 'reason');
    return reason ? `Cancelled (${reason}).` : 'Cancelled.';
  }
  if (run.status === 'RUNNING') return 'Running now. Step history should update shortly.';
  if (run.status === 'QUEUED') return 'Queued and waiting for worker pickup.';
  if (run.status === 'WAITING_APPROVAL') return "Waiting for a member's approval in Mission Control.";
  if (run.status === 'SUCCEEDED') {
    return readRuntimeStateString(run, 'summaryLine') ?? 'Completed successfully.';
  }
  return `Status: ${run.status}`;
}

function getEmailTriggerLink(run: ProcessRunListItem): { href: string; label: string } | null {
  const event = run.triggerEvent;
  if (!event || typeof event !== 'object' || Array.isArray(event)) return null;
  const record = event as Record<string, unknown>;
  if (record.type !== 'EMAIL_RECEIVED' || typeof record.messageContentId !== 'number') return null;
  const subject = typeof record.subject === 'string' && record.subject.trim() ? `: ${trimForUi(record.subject, 60)}` : '';
  return { href: toContentHref(String(record.messageContentId)), label: `View email${subject}` };
}

function getStepStatusHint(step: ProcessRunEntryListItem): string | null {
  if (step.status === 'FAILED') return step.error ? trimForUi(step.error, 180) : 'Step failed.';
  if (step.status === 'INFO') return 'Step update recorded.';
  return null;
}

function formatTriggerSummary(process: { triggerType: string; triggerDefinition: Record<string, unknown> }): string {
  const def = process.triggerDefinition;
  if (process.triggerType === 'TAG_APPLIED') {
    return `Tag Applied (tagId: ${def.tagId ?? 'N/A'})`;
  }
  if (process.triggerType === 'SCHEDULE') {
    return def.cron && def.timezone ? `Schedule (${def.cron} ${def.timezone})` : 'Schedule';
  }
  if (process.triggerType === 'EMAIL_RECEIVED') {
    return 'Email received (AI-routed by match criteria)';
  }
  if (process.triggerType === 'FILE_UPLOADED') {
    return 'File uploaded';
  }
  if (process.triggerType === 'EXTRACT_SCORING') {
    return 'Obligation scoring';
  }
  if (process.triggerType === 'MESSAGE_IMPORTED') {
    const directions = Array.isArray(def.directions) ? (def.directions as string[]) : [];
    return `Message Imported (directions: ${directions.length > 0 ? directions.join(', ') : 'all'})`;
  }
  if (process.triggerType === 'XERO_EVENT') {
    const eventTypes = Array.isArray(def.eventTypes) ? (def.eventTypes as string[]) : [];
    return `Xero ${typeof def.eventCategory === 'string' ? def.eventCategory : 'record'} (${eventTypes.length > 0 ? eventTypes.join(', ') : 'no event types'})`;
  }
  if (process.triggerType === 'DOCUMENT_UPDATED') {
    const scope = typeof def.rootDocumentCode === 'string' ? `scope '${def.rootDocumentCode}'` : 'any document';
    return `Document updated (tags group #${def.tagGroupId ?? 'N/A'}, ${scope})`;
  }
  return process.triggerType;
}

function getRunSummaryLine(steps: ProcessRunEntryListItem[]): string | null {
  for (let i = steps.length - 1; i >= 0; i -= 1) {
    const detail = steps[i].detail;
    if (detail && typeof detail.summaryLine === 'string') {
      return detail.summaryLine;
    }
  }
  return null;
}

export function Work4MeProcessDetail() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const selectedProcessId = id ?? null;
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [leaseApprovalPending, setLeaseApprovalPending] = useState(false);
  const [renewDays, setRenewDays] = useState('90');
  const [renewCap, setRenewCap] = useState('');
  const [graduateKeys, setGraduateKeys] = useState<string[]>([]);
  const { data: memberContext } = useUser();
  const isHubAdmin = memberContext?.isHubAdmin ?? false;

  const processesQuery = useQuery({ queryKey: ['processes'], queryFn: listProcesses });
  const processRunsQuery = useQuery({
    queryKey: ['process-runs', selectedProcessId],
    queryFn: () => listProcessRuns(selectedProcessId as string, 20),
    enabled: Boolean(selectedProcessId),
  });
  const processRunStepsQuery = useQuery({
    queryKey: ['process-run-entries', selectedProcessId, selectedRunId],
    queryFn: () => listProcessRunEntries(selectedProcessId as string, selectedRunId as number),
    enabled: Boolean(selectedProcessId && selectedRunId),
  });
  const processAgentOptionsQuery = useQuery({ queryKey: ['process-agent-options'], queryFn: listProcessAgentOptions });
  const processSkillOptionsQuery = useQuery({ queryKey: ['process-skill-options'], queryFn: listProcessSkillOptions });

  const toggleProcessMutation = useMutation({
    mutationFn: ({ mutId, enabled }: { mutId: string; enabled: boolean }) => updateProcessEnabled(mutId, enabled),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['processes'] });
    },
  });

  const saveProcessMutation = useMutation({
    mutationFn: (values: ProcessFormValues) => {
      const trigger =
        process?.triggerType === 'TAG_APPLIED'
          ? { type: 'TAG_APPLIED' as const, config: { tagId: values.tagId } }
          : process?.triggerType === 'MESSAGE_IMPORTED'
          ? { type: 'MESSAGE_IMPORTED' as const, config: { directions: values.directions } }
          : process?.triggerType === 'EMAIL_RECEIVED'
          ? { type: 'EMAIL_RECEIVED' as const, config: { matchCriteria: values.matchCriteria } }
          : process?.triggerType === 'SCHEDULE'
          ? {
              type: 'SCHEDULE' as const,
              config: {
                cron: values.cron,
                timezone: values.timezone,
                ...(values.runAsMemberId !== null ? { runAsMemberId: values.runAsMemberId } : {}),
              },
            }
          : process?.triggerType === 'XERO_EVENT'
          ? {
              type: 'XERO_EVENT' as const,
              config: {
                eventCategory: values.xeroEventCategory,
                eventTypes: values.xeroEventTypes,
                ...(values.xeroEventCategory === 'INVOICE' && values.xeroInvoiceTypes.length > 0
                  ? { invoiceTypes: values.xeroInvoiceTypes }
                  : {}),
                ...(values.xeroEventCategory === 'INVOICE' && values.xeroInvoiceStatuses.length > 0
                  ? { invoiceStatuses: values.xeroInvoiceStatuses }
                  : {}),
                ...(values.runAsMemberId !== null ? { runAsMemberId: values.runAsMemberId } : {}),
              },
            }
          : undefined;
      return updateProcess(id as string, {
        name: values.name,
        description: values.description,
        ...(values.agentId !== null ? { agentId: values.agentId } : {}),
        skillIds: values.skillIds,
        inputTemplate: values.inputTemplate,
        enabled: values.enabled,
        ...(trigger ? { trigger } : {}),
      });
    },
    onSuccess: async (_data, values) => {
      await queryClient.invalidateQueries({ queryKey: ['processes'] });
      toast.success(`Saved "${values.name}".`);
      setIsEditing(false);
    },
    onError: (error: unknown) => {
      toast.error(getAxiosErrorDisplayMessage(error, 'Failed to save process.'));
    },
  });

  const saveDeterministicMutation = useMutation({
    mutationFn: (values: DeterministicProcessFormValues) => {
      return updateProcess(id as string, {
        name: values.name,
        description: values.description,
        enabled: values.enabled,
      });
    },
    onSuccess: async (_data, values) => {
      await queryClient.invalidateQueries({ queryKey: ['processes'] });
      toast.success(`Saved "${values.name}".`);
      setIsEditing(false);
    },
    onError: (error: unknown) => {
      toast.error(getAxiosErrorDisplayMessage(error, 'Failed to save process.'));
    },
  });

  const leaseMutation = useMutation({
    mutationFn: ({ toolKey, input }: LeaseActionInput) => executeTool<Record<string, unknown>, unknown>(toolKey, input),
    onSuccess: async (result, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['processes'] });
      if (result.status === 'PENDING_APPROVAL') {
        setLeaseApprovalPending(true);
        toast.message('Queued for approval in Mission Control.');
        return;
      }
      if (result.status === 'EXECUTED') {
        setGraduateKeys([]);
        toast.success(variables.successText);
        return;
      }
      toast.error(result.error ?? `Lease action ended with status ${result.status}.`);
    },
    onError: (error: unknown) => {
      toast.error(getAxiosErrorDisplayMessage(error, 'Lease action failed.'));
    },
  });

  const deleteProcessMutation = useMutation({
    mutationFn: () => deleteProcess(selectedProcessId as string),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['processes'] });
      toast.success('Process deleted.');
      navigate('/work4me/processes');
    },
    onError: (error: unknown) => {
      toast.error(getAxiosErrorDisplayMessage(error, 'Failed to delete process.'));
    },
  });

  const membersQuery = useQuery({
    queryKey: ['admin-members'],
    queryFn: listMembers,
    enabled: isHubAdmin && isEditing,
  });

  const memberOptions = useMemo(
    () =>
      (membersQuery.data?.members ?? []).map((member) => ({
        id: member.id,
        label: `${member.firstName} ${member.surname}`.trim() || member.email,
      })),
    [membersQuery.data?.members]
  );

  const process = useMemo(() => {
    const items = processesQuery.data?.items ?? [];
    return items.find((item) => item.id === selectedProcessId);
  }, [processesQuery.data?.items, selectedProcessId]);

  const selectedRunCode = useMemo(() => {
    if (!selectedRunId) return null;
    const runs = processRunsQuery.data?.items ?? [];
    return runs.find((run) => run.id === selectedRunId)?.code ?? null;
  }, [processRunsQuery.data?.items, selectedRunId]);

  const agentOptions = processAgentOptionsQuery.data?.items ?? [];
  const skillOptions = processSkillOptionsQuery.data?.items ?? [];
  const linkedAgent = agentOptions.find((a) => a.id === process?.agentId);
  const linkedSkills = skillOptions.filter((s) => process?.skillIds.includes(s.id));

  const isDeterministic = process?.executionKind === 'DETERMINISTIC';

  const formInitialValues: ProcessFormValues | null = useMemo(() => {
    if (!process) return null;
    return {
      name: process.name,
      description: process.description ?? '',
      triggerType: process.triggerType,
      tagId: process.triggerDefinition.tagId ?? 0,
      directions: process.triggerDefinition.directions ?? [],
      matchCriteria: process.triggerDefinition.matchCriteria ?? '',
      cron: process.triggerDefinition.cron ?? '',
      timezone: process.triggerDefinition.timezone ?? '',
      runAsMemberId: process.triggerDefinition.runAsMemberId ?? null,
      xeroEventCategory: process.triggerDefinition.eventCategory ?? 'INVOICE',
      xeroEventTypes: process.triggerDefinition.eventTypes ?? [],
      xeroInvoiceTypes: process.triggerDefinition.invoiceTypes ?? [],
      xeroInvoiceStatuses: process.triggerDefinition.invoiceStatuses ?? [],
      agentId: process.agentId,
      systemAgent:
        process.systemAgentKey !== null && process.systemAgentName !== null
          ? { key: process.systemAgentKey, name: process.systemAgentName }
          : null,
      skillIds: process.skillIds,
      inputTemplate: process.inputTemplate ?? '',
      enabled: process.status === 'ENABLED',
    };
  }, [process]);

  const deterministicInitialValues: DeterministicProcessFormInitialValues | null = useMemo(() => {
    if (!process) return null;
    return {
      name: process.name,
      description: process.description ?? '',
      enabled: process.status === 'ENABLED',
      handlerKey: process.handlerKey,
      triggerType: process.triggerType,
    };
  }, [process]);

  useEffect(() => {
    if (!selectedProcessId || selectedRunId !== null) return;
    const runParam = searchParams.get('run');
    if (runParam) {
      const rid = Number(runParam);
      const match = processRunsQuery.data?.items?.find((r) => r.id === rid);
      if (match) {
        setSelectedRunId(rid);
        return;
      }
    }
    const firstRunId = processRunsQuery.data?.items?.[0]?.id;
    if (firstRunId) setSelectedRunId(firstRunId);
  }, [processRunsQuery.data, searchParams, selectedProcessId, selectedRunId]);

  if (!selectedProcessId) {
    return (
      <div className="rounded-lg border bg-card p-5 m-4">
        <p className="text-sm text-destructive">Invalid process id.</p>
      </div>
    );
  }

  if (processesQuery.isLoading) {
    return (
      <div className="rounded-lg border bg-card p-5 m-4">
        <p className="text-sm text-muted-foreground">Loading process...</p>
      </div>
    );
  }

  if (processesQuery.isError || !process) {
    return (
      <div className="rounded-lg border bg-card p-5 m-4">
        <p className="text-sm text-destructive">Unable to load process detail.</p>
      </div>
    );
  }

  return (
    <div className="-mx-4 py-2">
      <div className="px-4 py-3 border-b bg-muted/20">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-light tracking-wide">Process: {process.name}</h2>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Badge variant="outline">{process.status}</Badge>
              {isDeterministic ? <Badge variant="secondary">Automatic</Badge> : null}
              {!isEditing && (
                <Button type="button" variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
              {isEditing && (
                <Button type="button" variant="outline" size="sm" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
              )}
              <Button type="button" variant="outline" size="sm" onClick={() => navigate('/work4me/processes')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Processes
              </Button>
              {!isDeterministic && !isEditing && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => setConfirmDeleteOpen(true)}
                >
                  Delete
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete process</DialogTitle>
            <DialogDescription>
              Delete '{process.name}'? It will no longer trigger. Past run history is kept for the record.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteProcessMutation.mutate()}
              disabled={deleteProcessMutation.isPending}
            >
              {deleteProcessMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid gap-4 px-4 pt-4">
        {isEditing && isDeterministic && deterministicInitialValues ? (
          <div className="rounded-md border p-3">
            <Work4MeDeterministicProcessForm
              initialValues={deterministicInitialValues}
              isSubmitting={saveDeterministicMutation.isPending}
              onSubmit={async (values) => {
                saveDeterministicMutation.mutate(values);
              }}
            />
          </div>
        ) : isEditing && formInitialValues ? (
          <div className="rounded-md border p-3">
            <Work4MeProcessForm
              initialValues={formInitialValues}
              isSubmitting={saveProcessMutation.isPending}
              submitLabel="Save Process"
              submittingLabel="Saving..."
              agentOptions={agentOptions}
              skillOptions={skillOptions}
              memberOptions={memberOptions}
              onSubmit={async (values) => {
                saveProcessMutation.mutate(values);
              }}
            />
          </div>
        ) : (
          <>
            <div className="rounded-md border p-3">
              <p className="text-xs tracking-wide text-muted-foreground">Description</p>
              <p className="mt-1 text-sm">{process.description ?? 'No description'}</p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-md border p-3">
                <p className="text-xs tracking-wide text-muted-foreground">Trigger</p>
                <p className="mt-1 text-sm">{formatTriggerSummary(process)}</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-xs tracking-wide text-muted-foreground">Agent</p>
                {process.systemAgentKey !== null ? (
                  <div className="mt-1">
                    <p className="text-sm inline-flex items-center gap-1">
                      {process.systemAgentName}
                      <Badge variant="outline" className="text-[10px] ml-1">System</Badge>
                    </p>
                    {process.systemAgentDescription ? (
                      <p className="mt-1 text-xs text-muted-foreground">{process.systemAgentDescription}</p>
                    ) : null}
                  </div>
                ) : process.executionKind === 'DETERMINISTIC' || process.agentId === null ? (
                  <p className="mt-1 text-sm text-muted-foreground">—</p>
                ) : linkedAgent ? (
                  <AppLink to={`/work4me/agents/${linkedAgent.id}`} className="mt-1 text-sm text-primary hover:underline inline-flex items-center gap-1">
                    {linkedAgent.name}
                    <Badge variant="outline" className="text-[10px] ml-1">{linkedAgent.ownerScope}</Badge>
                  </AppLink>
                ) : (
                  <p className="mt-1 text-sm text-muted-foreground">Agent ID {process.agentId}</p>
                )}
              </div>
            </div>

            {linkedSkills.length > 0 && (
              <div className="rounded-md border p-3">
                <p className="text-xs tracking-wide text-muted-foreground">Skills</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {linkedSkills.map((skill) => (
                    <AppLink key={skill.id} to={`/work4me/skills/${skill.id}`} className="text-sm text-primary hover:underline">
                      {skill.name}
                    </AppLink>
                  ))}
                </div>
              </div>
            )}

            {isDeterministic && (
              <div className="rounded-md border p-3">
                <p className="text-xs tracking-wide text-muted-foreground">Handler key</p>
                <p className="mt-1 text-sm font-mono">{process.handlerKey ?? '—'}</p>
              </div>
            )}

            {process.inputTemplate && (
              <div className="rounded-md border p-3">
                <p className="text-xs tracking-wide text-muted-foreground">Input Template</p>
                <pre className="mt-1 text-sm whitespace-pre-wrap break-all bg-muted/30 rounded p-2">{process.inputTemplate}</pre>
              </div>
            )}

            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <p className="text-sm font-medium">Enabled</p>
                <p className="text-xs text-muted-foreground">Disabled processes stay stored but will not trigger.</p>
              </div>
              <Switch
                checked={process.status === 'ENABLED'}
                onCheckedChange={(checked) => {
                  if (process.status === (checked ? 'ENABLED' : 'DISABLED')) return;
                  toggleProcessMutation.mutate({ mutId: process.id, enabled: checked });
                }}
                disabled={toggleProcessMutation.isPending}
              />
            </div>

            <p className="text-xs text-muted-foreground">
              Last run: {formatTimestamp(process.lastRunAt)} · Updated: {formatTimestamp(process.updatedAt)}
            </p>
          </>
        )}

        <div className="rounded-md border p-3 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs tracking-wide text-muted-foreground">Lease</p>
            <Badge variant={leaseStateVariant(process.lease.state)}>{leaseStateLabel(process.lease.state)}</Badge>
          </div>
          {process.lease.state === 'none' ? (
            <p className="text-xs text-muted-foreground">Platform process — no lease.</p>
          ) : (
            <>
              <div className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
                <p className="text-muted-foreground">Issued: <span className="text-foreground">{formatTimestamp(process.lease.issuedAt)}</span></p>
                <p className="text-muted-foreground">Expires: <span className="text-foreground">{process.lease.expiresAt ? formatTimestamp(process.lease.expiresAt) : 'no expiry'}</span></p>
                <p className="text-muted-foreground">Spend: <span className="text-foreground">{formatAud(process.lease.spendAud, '0')} of {formatAud(process.lease.spendCapAud, 'no cap')}</span></p>
                <p className="text-muted-foreground">Clean run streak: <span className="text-foreground">{process.lease.cleanRunStreak} of {LEASE_GRADUATION_MIN_CLEAN_RUNS} minimum</span></p>
                <p className="text-muted-foreground">Rejected approvals this lease: <span className="text-foreground">{process.lease.rejectedApprovalsInLease}</span></p>
                <p className="text-muted-foreground">Runs this lease: <span className="text-foreground">{process.lease.runsInLease}</span></p>
              </div>

              <div className="space-y-1">
                <p className="text-xs font-medium">Runs without asking</p>
                {process.lease.autonomousToolKeys.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Every approval-gated tool still asks.</p>
                ) : (
                  process.lease.autonomousToolKeys.map((toolKey) => (
                    <div key={toolKey} className="flex items-start justify-between gap-2 text-xs">
                      <p className="min-w-0">
                        <span className="font-mono">{toolKey}</span>
                        {process.lease.toolDescriptions[toolKey] ? (
                          <span className="block text-muted-foreground">{trimForUi(process.lease.toolDescriptions[toolKey], 160)}</span>
                        ) : null}
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={leaseMutation.isPending}
                        onClick={() =>
                          leaseMutation.mutate({
                            toolKey: 'processes.revokeLeaseAutonomy',
                            input: { processId: process.id, toolKeys: [toolKey] },
                            successText: `'${process.name}' asks again for ${toolKey}.`,
                          })
                        }
                      >
                        Revoke
                      </Button>
                    </div>
                  ))
                )}
              </div>

              <div className="space-y-1">
                <p className="text-xs font-medium">Ready to graduate</p>
                {process.lease.candidateToolKeys.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No candidates yet — approve the same tool across a clean streak of {LEASE_GRADUATION_MIN_CLEAN_RUNS} runs.
                  </p>
                ) : (
                  <>
                    {process.lease.candidateToolKeys.map((toolKey) => (
                      <label key={toolKey} className="flex items-start gap-2 text-xs">
                        <Checkbox
                          checked={graduateKeys.includes(toolKey)}
                          onCheckedChange={(checked) =>
                            setGraduateKeys((prev) =>
                              checked === true ? [...prev, toolKey] : prev.filter((key) => key !== toolKey)
                            )
                          }
                        />
                        <span className="min-w-0">
                          <span className="font-mono">{toolKey}</span>
                          {process.lease.toolDescriptions[toolKey] ? (
                            <span className="block text-muted-foreground">{trimForUi(process.lease.toolDescriptions[toolKey], 160)}</span>
                          ) : null}
                        </span>
                      </label>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={leaseMutation.isPending || graduateKeys.length === 0}
                      onClick={() =>
                        leaseMutation.mutate({
                          toolKey: 'processes.graduateLease',
                          input: { processId: process.id, toolKeys: graduateKeys },
                          successText: `'${process.name}' now runs ${graduateKeys.join(', ')} without asking.`,
                        })
                      }
                    >
                      Graduate selected
                    </Button>
                  </>
                )}
              </div>

              <div className="space-y-1">
                <p className="text-xs font-medium">Renew</p>
                <div className="flex flex-wrap items-end gap-2">
                  <label className="text-xs text-muted-foreground">
                    Extend by (days)
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      value={renewDays}
                      onChange={(event) => setRenewDays(event.target.value)}
                      className="mt-1 h-8 w-28"
                    />
                  </label>
                  <label className="text-xs text-muted-foreground">
                    New spend cap (A$, blank keeps)
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={renewCap}
                      onChange={(event) => setRenewCap(event.target.value)}
                      className="mt-1 h-8 w-36"
                    />
                  </label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={leaseMutation.isPending || !(Number(renewDays) >= 1 && Number(renewDays) <= 365)}
                    onClick={() =>
                      leaseMutation.mutate({
                        toolKey: 'processes.renewLease',
                        input: {
                          processId: process.id,
                          extendDays: Number(renewDays),
                          ...(renewCap.trim() === '' ? {} : { spendCapAud: Number(renewCap) }),
                        },
                        successText: `Renewed the lease for '${process.name}'.`,
                      })
                    }
                  >
                    Renew lease
                  </Button>
                </div>
              </div>

              {leaseApprovalPending ? (
                <AppLink to={MISSION_CONTROL_APPROVALS_HREF} className="text-xs text-primary underline">
                  Pending approvals (Mission Control)
                </AppLink>
              ) : null}
            </>
          )}
        </div>

        <div className="rounded-md border p-3 space-y-2">
          <p className="text-xs tracking-wide text-muted-foreground">Recent process runs</p>
          {isDeterministic ? (
            <p className="text-xs text-muted-foreground">Handler: <span className="font-mono">{process.handlerKey ?? '—'}</span></p>
          ) : null}
          {(processRunsQuery.data?.items ?? []).map((run) => (
            <div
              key={run.id}
              className={`rounded border bg-background transition-colors ${selectedRunId === run.id ? 'border-primary' : 'hover:bg-muted/30'}`}
            >
              <button
                type="button"
                className="w-full rounded-t border-b border-transparent p-2 space-y-1 text-left"
                onClick={() => {
                  setSelectedRunId(run.id);
                  setSearchParams({ run: String(run.id) });
                }}
              >
                <div className="flex items-center justify-between gap-2">
                  <Badge variant={runStatusVariant(run.status)}>{run.status}</Badge>
                  <span className="text-xs text-muted-foreground">{formatTimestamp(run.createdAt)}</span>
                </div>
                <p className="text-xs text-muted-foreground">Run content {run.code}</p>
                <p className={`text-xs ${run.status === 'FAILED' ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {getRunStatusHint(run)}
                </p>
                <p className="text-xs text-muted-foreground">Queued: {formatTimestamp(run.queuedAt)}</p>
                <p className="text-xs text-muted-foreground">Finished: {formatTimestamp(run.finishedAt)}</p>
              </button>
              <div className="flex flex-wrap gap-x-3 gap-y-1 px-2 pb-2 pt-1">
                <AppLink to={toContentHref(run.code)} className="text-xs text-primary underline">
                  Open run in explorer
                </AppLink>
                {(() => {
                  const emailLink = getEmailTriggerLink(run);
                  return emailLink ? (
                    <AppLink to={emailLink.href} className="text-xs text-primary underline">
                      {emailLink.label}
                    </AppLink>
                  ) : null;
                })()}
                {isHubAdmin && run.agentRunId ? (
                  <AppLink to={toMissionControlRunHref(run.agentRunId)} className="text-xs text-primary underline">
                    Agent execution trace (Mission Control)
                  </AppLink>
                ) : null}
                {isHubAdmin && run.status === 'WAITING_APPROVAL' ? (
                  <AppLink to={MISSION_CONTROL_APPROVALS_HREF} className="text-xs text-primary underline">
                    Pending approvals (Mission Control)
                  </AppLink>
                ) : null}
                <ProcessRunCancelAction runId={run.id} kind="provenance" status={run.status} />
              </div>
            </div>
          ))}
          {(processRunsQuery.data?.executions ?? []).map((execution) => (
            <div key={execution.id} className="rounded border bg-background p-2 space-y-1">
              <div className="flex items-center justify-between gap-2">
                <Badge variant={runStatusVariant(execution.status)}>{execution.status}</Badge>
                <span className="text-xs text-muted-foreground">{formatTimestamp(execution.createdAt)}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Ran in {execution.apexTitle ?? execution.apexCode ?? `apex ${execution.apexProvenanceId}`}
              </p>
              {execution.summaryLine ? (
                <p className="text-xs text-muted-foreground">{execution.summaryLine}</p>
              ) : null}
              {execution.error ? <p className="text-xs text-destructive">{execution.error}</p> : null}
              {execution.apexCode ? (
                <AppLink to={toContentHref(execution.apexCode)} className="text-xs text-primary underline">
                  Open originating activity
                </AppLink>
              ) : null}
              <ProcessRunCancelAction runId={execution.id} kind="execution" status={execution.status} />
            </div>
          ))}
          {processRunsQuery.data?.items.length === 0 && (processRunsQuery.data?.executions ?? []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No runs yet for this process.</p>
          ) : null}
        </div>

        {selectedRunId ? (
          <div className="rounded border p-2 space-y-2 bg-background">
            <p className="text-xs font-medium">Step history</p>
            <p className="text-[11px] text-muted-foreground leading-snug">
              Ordered steps for run {selectedRunCode ?? selectedRunId}. Full detail and I/O is on the run&apos;s content page.
            </p>
            {getRunSummaryLine(processRunStepsQuery.data?.items ?? []) ? (
              <p className="text-xs">{getRunSummaryLine(processRunStepsQuery.data?.items ?? [])}</p>
            ) : null}
            {(processRunStepsQuery.data?.items ?? []).map((step) => (
              <div key={step.id} className="rounded border p-2 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-medium">#{step.sequence} {step.kind}</p>
                  <Badge variant={stepStatusVariant(step.status)}>{step.status}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">{formatTimestamp(step.createdAt)}</p>
                {getStepStatusHint(step) ? (
                  <p className={`text-xs ${step.status === 'FAILED' ? 'text-destructive' : 'text-muted-foreground'}`}>
                    {getStepStatusHint(step)}
                  </p>
                ) : null}
                {step.error ? <p className="text-xs text-destructive">{step.error}</p> : null}
                {step.detail ? (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-muted-foreground">Payload</summary>
                    <pre className="mt-1 text-[11px] whitespace-pre-wrap break-all max-h-32 overflow-y-auto">
                      {JSON.stringify(step.detail, null, 2)}
                    </pre>
                  </details>
                ) : null}
              </div>
            ))}
            {processRunStepsQuery.data?.items.length === 0 ? (
              <p className="text-xs text-muted-foreground">No steps recorded for this run.</p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
